import { formatIsoDate } from '@src/shared/lib';
import type { HolidayDate } from '../types';

type HolidayLookup = {
  globalDates: Set<string>;
  isHolidayDate: (date: Date | string, branchId?: number | null) => boolean;
  tooltipsByDate: Map<string, string>;
};

function getHolidaySpanDays(days: number): number {
  if (!Number.isFinite(days)) {
    return 1;
  }

  return Math.max(1, Math.ceil(days));
}

export function addDaysToIsoDate(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  const nextDate = new Date(year, month - 1, day);
  nextDate.setDate(nextDate.getDate() + days);
  return formatIsoDate(nextDate);
}

export function holidayAppliesToBranch(
  holiday: HolidayDate,
  branchId?: number | null,
) {
  return holiday.branchId === null || holiday.branchId === branchId;
}

export function getHolidayCoveredDates(holiday: HolidayDate): string[] {
  const coveredDates: string[] = [];

  for (let offset = 0; offset < getHolidaySpanDays(holiday.days); offset++) {
    coveredDates.push(addDaysToIsoDate(holiday.date, offset));
  }

  return coveredDates;
}

export function getHolidayExclusiveEndDate(holiday: HolidayDate): string {
  return addDaysToIsoDate(holiday.date, getHolidaySpanDays(holiday.days));
}

export function buildHolidayLookup(holidays: HolidayDate[]): HolidayLookup {
  const globalDates = new Set<string>();
  const datesByBranch = new Map<number, Set<string>>();
  const tooltipsByDate = new Map<string, string>();

  for (const holiday of holidays) {
    const coveredDates = getHolidayCoveredDates(holiday);

    if (holiday.branchId === null) {
      for (const date of coveredDates) {
        globalDates.add(date);
      }
    } else {
      const branchDates =
        datesByBranch.get(holiday.branchId) ?? new Set<string>();
      for (const date of coveredDates) {
        branchDates.add(date);
      }
      datesByBranch.set(holiday.branchId, branchDates);
    }

    if (holiday.name) {
      const label = holiday.branchName
        ? `${holiday.name} (${holiday.branchName})`
        : holiday.name;

      for (const date of coveredDates) {
        const existing = tooltipsByDate.get(date);
        tooltipsByDate.set(date, existing ? `${existing}\n${label}` : label);
      }
    }
  }

  return {
    globalDates,
    tooltipsByDate,
    isHolidayDate: (date: Date | string, branchId?: number | null) => {
      const dateKey = formatIsoDate(date);
      if (globalDates.has(dateKey)) {
        return true;
      }

      if (branchId == null) {
        return false;
      }

      return datesByBranch.get(branchId)?.has(dateKey) ?? false;
    },
  };
}
